/*
 * -------------------------------------------------------------------------------
 * Imports - External
 * -------------------------------------------------------------------------------
 */
import { Box, styled } from '@mui/material';
import { FC, useCallback, useContext, useState } from 'react';

/*
* -------------------------------------------------------------------------------
* Imports - Internal
* -------------------------------------------------------------------------------
*/
import Dropzone from '@/components/inputs/Dropzone';
import Hidden from '@/components/layout/Hidden';
import PdfViewer from '@/components/pdf/PdfViewer';
import AlertContext from '@/context/AlertContext';

/*
 * -------------------------------------------------------------------------------
 * Components
 * -------------------------------------------------------------------------------
 */
const Container = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '100vh', 
}));

const DropzoneContainer = styled(Box)(({ theme }) => ({
    width: '90%',
    backgroundColor: '#fff',
    borderRadius: '15px',
    height: '25vh',
}));

/*
 * -------------------------------------------------------------------------------
 * Components - Main
 * -------------------------------------------------------------------------------
 */
const RedactUploadPage: FC = () => {
    
    const { addAlert } = useContext(AlertContext);

    const [file, setFile] = useState<File | undefined>(undefined);

    const onConfirm = useCallback((redactedFile: File) => {
        // Create link to download file
        const blob = new Blob([redactedFile], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `${redactedFile.name}`;
        link.href = url;
        link.click();

        // Remove selected file
        setFile(undefined);

        addAlert({
            content: 'Document has been redacted successfully!',
            timeout: 5000,
            severity: 'success',
            dismissable: true,
        });

    }, [setFile, addAlert]);

    const onCancel = useCallback(() => {
        setFile(undefined);
    }, [setFile]);

    const [viewerLoading, setViewerLoading] = useState<boolean>(true);

    const onConfirmClicked = useCallback(() => {
        setViewerLoading(true);
    }, [setViewerLoading]);

    const onPdfLoaded = useCallback(() => {
        setViewerLoading(false);
    }, [setViewerLoading]);

    const onRedactionFinished = useCallback(() => {
        setViewerLoading(false);
    }, [setViewerLoading]);

    return (
        <Container>
            
            <Hidden hide={!!file} />
            {
                file ? 
                    <PdfViewer 
                        file={file} 
                        onConfirm={onConfirm} 
                        onCancel={onCancel} 
                        loading={viewerLoading}
                        onConfirmClicked={onConfirmClicked}
                        onPdfLoaded={onPdfLoaded}
                        onRedactionFinished={onRedactionFinished}
                    /> :
                    <DropzoneContainer>
                        <Dropzone 
                            secondaryText="PDF documents only accepted"
                            setFile={(file: File) => setFile(file)}
                        />
                    </DropzoneContainer>
            }
        </Container>
    )
}

export default RedactUploadPage